import { useState, useEffect } from "react";
import { Sparkles, Send, Info, RefreshCw, Share2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";


const PLATFORMS = [
  { key: "twitter", label: "X/Twitter" },
  { key: "linkedin", label: "LinkedIn" },
  { key: "facebook", label: "Facebook" },
  { key: "instagram", label: "Instagram" },
  { key: "tiktok", label: "TikTok" },
];


function StatusPill({ status }) {
  const map = {
    scheduled: { icon: Clock, tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Scheduled" },
    published: { icon: CheckCircle2, tone: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10", label: "Published" },
    failed: { icon: XCircle, tone: "text-rose-300 border-rose-500/40 bg-rose-500/10", label: "Failed" },
  };
  const cfg = map[status] || { icon: Info, tone: "text-slate-400 border-slate-700 bg-slate-800/30", label: status || "pending" };
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-sm border ${cfg.tone}`}>
      <Icon className="h-3 w-3" /> {cfg.label}
    </span>
  );
}


export default function BlotatoPanel({ opp }) {
  const [selected, setSelected] = useState(["twitter", "linkedin", "facebook"]);
  const [drafts, setDrafts] = useState({});
  const [posts, setPosts] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [blotatoConfigured, setBlotatoConfigured] = useState(null);

  useEffect(() => {
    if (!opp?.oid) return;
    api.get(`/opportunities/${opp.oid}/blotato/posts`).then((r) => setPosts(r.data || [])).catch(() => {});
  }, [opp?.oid]);

  // Part C contextual first-use: detect whether the subscriber has connected Blotato.
  useEffect(() => {
    api.get("/integrations/me").then((r) => setBlotatoConfigured(!!r.data?.blotato?.configured)).catch(() => setBlotatoConfigured(null));
  }, []);

  const toggle = (k) => setSelected((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/blotato/generate`, { platforms: selected });
      setDrafts(r.data.drafts || {});
      toast.success("Posts drafted by Claude");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft generation failed");
    } finally {
      setGenerating(false);
    }
  };

  const schedule = async () => {
    const list = Object.entries(drafts)
      .filter(([, text]) => text && text.trim())
      .map(([platform, text]) => ({ platform, text }));
    if (!list.length) { toast.error("No drafts to schedule"); return; }
    setScheduling(true);
    try {
      const r = await api.post(`/opportunities/${opp.oid}/blotato/schedule`, { posts: list });
      const okCount = (r.data.scheduled || []).length;
      const failCount = (r.data.errors || []).length;
      if (okCount) toast.success(`${okCount} post${okCount > 1 ? "s" : ""} scheduled on Blotato`);
      if (failCount) toast.error(`${failCount} platform${failCount > 1 ? "s" : ""} failed ,  see status below`);
      setDrafts({});
      const list2 = await api.get(`/opportunities/${opp.oid}/blotato/posts`);
      setPosts(list2.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Schedule failed");
    } finally {
      setScheduling(false);
    }
  };

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/blotato/sync-status`);
      toast.success(`Polled ${r.data.polled}, updated ${r.data.updated}`);
      const list = await api.get(`/opportunities/${opp.oid}/blotato/posts`);
      setPosts(list.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="ae-card p-5 space-y-4" data-testid="blotato-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
          <span>Social Media Automation</span>
          <span title="Blotato-powered scheduling. Claude drafts platform-tailored posts; scheduling picks next-free-slot. Auto-fires when the opp advances to Contract Signed." className="cursor-help text-slate-600 hover:text-emerald-300"><Info className="h-3 w-3" /></span>
        </div>
        <button
          onClick={sync}
          disabled={syncing}
          className="text-xs inline-flex items-center gap-2 px-2 py-1 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-300 hover:text-emerald-300 disabled:opacity-50"
          data-testid="blotato-sync-btn"
          title="Poll Blotato for latest post statuses"
        >
          <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
          Sync status
        </button>
      </div>

      {/* Part C contextual first-use prompt: surface Blotato connect here, not in upfront onboarding. */}
      {blotatoConfigured === false && (
        <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2.5 flex items-start gap-2" data-testid="blotato-connect-prompt">
          <Share2 className="h-4 w-4 text-amber-300 shrink-0 mt-0.5" />
          <div className="text-xs text-amber-100/90">
            <span className="font-semibold text-amber-200">Connect Blotato to post to your socials.</span>{" "}
            You can draft posts here, but scheduling needs your own Blotato key. Add it in{" "}
            <a href="/app/settings" className="text-emerald-300 underline hover:text-emerald-200" data-testid="blotato-connect-prompt-link">
              Settings → Integrations → Optional connectors
            </a>. Acquigine&apos;s own promotional post still fires on Contract Signed.
          </div>
        </div>
      )}

      {/* Platform picker */}
      <div className="flex flex-wrap gap-2">
        {PLATFORMS.map((p) => (
          <button
            key={p.key}
            data-testid={`blotato-platform-${p.key}`}
            onClick={() => toggle(p.key)}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${
              selected.includes(p.key)
                ? "border-emerald-500/60 bg-emerald-500/10 text-emerald-300"
                : "border-white/10 text-slate-400 hover:border-white/20"
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <button
          onClick={generate}
          disabled={generating || !selected.length}
          data-testid="blotato-generate-btn"
          className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50"
        >
          <Sparkles className={`h-3.5 w-3.5 ${generating ? "animate-pulse" : ""}`} />
          {generating ? "Drafting…" : "Draft posts via Claude"}
        </button>
        {Object.keys(drafts).length > 0 && (
          <button
            onClick={schedule}
            disabled={scheduling}
            data-testid="blotato-schedule-btn"
            className="text-xs inline-flex items-center gap-2 px-3 py-2 rounded-sm border border-white/10 hover:border-emerald-500/40 text-slate-200 hover:text-emerald-300 disabled:opacity-50"
          >
            <Send className={`h-3.5 w-3.5 ${scheduling ? "animate-pulse" : ""}`} />
            {scheduling ? "Scheduling…" : "Schedule on Blotato"}
          </button>
        )}
      </div>

      {/* Drafts editor */}
      {Object.keys(drafts).length > 0 && (
        <div className="space-y-2" data-testid="blotato-drafts">
          {Object.entries(drafts).map(([plat, text]) => (
            <div key={plat} className="border border-white/8 rounded-sm p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">{plat}</div>
              <textarea
                value={text}
                onChange={(e) => setDrafts({ ...drafts, [plat]: e.target.value })}
                rows={3}
                className="w-full bg-transparent text-sm text-slate-200 resize-none outline-none border border-transparent focus:border-emerald-500/40 rounded-sm p-2"
              />
            </div>
          ))}
        </div>
      )}

      {/* Scheduled/published posts */}
      {posts.length > 0 && (
        <div className="pt-3 border-t border-white/8 space-y-2" data-testid="blotato-posts-list">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Recent posts</div>
          {posts.slice(-6).reverse().map((p, i) => (
            <div key={i} className="text-xs flex items-start gap-2 py-1">
              <Share2 className="h-3 w-3 text-slate-500 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-slate-400 uppercase tracking-wider text-[10px]">{p.platform}</span>
                  <StatusPill status={p.status} />
                  <span className="text-slate-600 text-[10px]">{p.scheduled_time === "next_free_slot" ? "next free slot" : (p.scheduled_time || "").slice(0, 16)}</span>
                </div>
                <div className="text-slate-300 truncate mt-0.5" title={p.text}>{p.text}</div>
                {p.error && <div className="text-rose-400 text-[10px] mt-0.5">{p.error.slice(0, 200)}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {!posts.length && !Object.keys(drafts).length && (
        <div className="text-xs text-slate-500 pt-2">
          No posts yet. Draft with Claude, edit inline, then schedule on Blotato. Auto-fires on Contract Signed.
        </div>
      )}
    </div>
  );
}
